// frontend-crm/src/pages/SuperAdmin/SuperAdminLayout.js
import React, { useState, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Divider, Chip, Tooltip, IconButton, useTheme
} from '@mui/material';
import {
  Business as BusinessIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon,
  BarChart as StatsIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
} from '@mui/icons-material';
import { ColorModeContext } from '../../App';

const DRAWER_WIDTH = 260;
const DRAWER_COLLAPSED = 72;

const NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/superadmin' },
  { label: 'Empresas', icon: <BusinessIcon />, path: '/superadmin/empresas' },
  { label: 'Usuarios', icon: <PeopleIcon />, path: '/superadmin/usuarios' },
  { label: 'Bots WhatsApp', icon: <WhatsAppIcon />, path: '/superadmin/bots' },
  { label: 'Estadísticas', icon: <StatsIcon />, path: '/superadmin/stats' },
  { label: 'App Branding', icon: <SettingsIcon />, path: '/superadmin/branding' },
];

export default function SuperAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const drawerWidth = collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* ── Sidebar ── */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Logo/Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minHeight: 72,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'transparent',
              border: `2px solid ${theme.palette.primary.main}`,
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            <AdminIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          </Avatar>
          {!collapsed && (
            <Box sx={{ overflow: 'hidden' }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: theme.palette.text.primary, lineHeight: 1.2, whiteSpace: 'nowrap' }}
              >
                SuperAdmin
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                Panel de Control
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{ ml: 'auto', color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary }, flexShrink: 0 }}
            size="small"
          >
            {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Nav Items */}
        <List sx={{ flex: 1, px: 1, pt: 1 }}>
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/superadmin' && location.pathname.startsWith(item.path));
            return (
              <Tooltip key={item.path} title={collapsed ? item.label : ''} placement="right">
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      borderRadius: 2,
                      py: 1.2,
                      px: collapsed ? 1.5 : 2,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      bgcolor: isActive ? `${theme.palette.primary.main}1A` : 'transparent',
                      border: isActive ? `1px solid ${theme.palette.primary.main}4D` : '1px solid transparent',
                      '&:hover': {
                        bgcolor: `${theme.palette.primary.main}1A`,
                        border: `1px solid ${theme.palette.primary.main}33`,
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                        minWidth: collapsed ? 'auto' : 40,
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                          whiteSpace: 'nowrap',
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}
        </List>

        <Divider sx={{ borderColor: theme.palette.divider, mx: 1 }} />

        {/* Logout */}
        <List sx={{ px: 1, pb: 2 }}>
          <Tooltip title={collapsed ? 'Cerrar Sesión' : ''} placement="right">
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  px: collapsed ? 1.5 : 2,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  '&:hover': { bgcolor: 'rgba(255, 72, 72, 0.1)' },
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.error.main, minWidth: collapsed ? 'auto' : 40, justifyContent: 'center' }}>
                  <LogoutIcon />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary="Cerrar Sesión"
                    primaryTypographyProps={{ fontSize: '0.875rem', color: theme.palette.error.main, whiteSpace: 'nowrap' }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </Tooltip>
        </List>
      </Drawer>

      {/* ── Main Content ── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: '100vh',
          bgcolor: theme.palette.background.default,
          overflow: 'auto',
          transition: 'margin-left 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper }}>
           <IconButton onClick={colorMode.toggleColorMode} color="inherit">
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon sx={{ color: theme.palette.text.primary }} />}
            </IconButton>
        </Box>
        <Outlet />
      </Box>
    </Box>
  );
}
